// Regime B fill ("B2B engine/09_price_visibility.md"). No-op for retail buyers
// - gated on the same pre-paint flag as the CSS. Fetches contract prices for
// every [data-b2b-price] node on the page and writes them in; on 401 or any
// failure, marks the node data-b2b-signin so the CSS shows "Sign in to see
// your price".
//
// The invariant is narrower than "never unhide the retail node": it is never
// show a retail figure AS this buyer's contracted price. A variant this buyer
// has no contract on is not a security-sensitive case - the retail price
// already rendered by Liquid inside this wrapper is simply the honest,
// correct price to show, same as any retail shopper sees, so that branch
// reveals it as-is rather than hiding it. Only the source==='retail' ->
// data-b2b-filled path is new here; the contract-price injection path below
// it is unchanged.
//
// Also sets data-b2b-variant-contracted on <html> once resolved, which
// base.css uses to gate the native buy button per *selected variant* (not
// per product - see the comment there). And when the current variant turns
// out uncontracted, tags the native cart (so a retail order this buyer goes
// on to place is still visible as theirs in admin) and fires a GA4 event -
// this buyer is a known B2B account browsing outside their contract range,
// which the business wants visibility into even though nothing here blocks
// the purchase.
(function () {
  'use strict';
  if (document.documentElement.dataset.b2b !== '1') return;

  var PREFIX = '/apps/portal';

  // Session lives in ?stok=, not a cookie (App Proxy drops Set-Cookie - see
  // b2b.js / portal.js's own withTok). This file runs on the PDP, a page the
  // login redirect never visits, so its only source for the token is the copy
  // portal.js saved to localStorage on sign-in.
  function withTok(url) {
    var t;
    try { t = localStorage.getItem('mgp_b2b_stok'); } catch (e) { t = null; }
    if (!t) return url;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'stok=' + encodeURIComponent(t);
  }

  // ponytail: fires on every fill() resolution while uncontracted (once per
  // PDP load/variant change) rather than de-duplicating against previously
  // sent state. Both calls are idempotent (cart attribute: same value
  // written twice is a no-op; GA4: an extra scoped event isn't harmful) and
  // this only runs for a signed-in B2B session on an uncontracted variant -
  // a rare path, not a hot loop - so tracking whether it already ran isn't
  // worth the extra state.
  function trackUncontractedVisit(companyId, companyName, priceInfo) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { b2b_company: companyName || companyId, b2b_company_id: companyId } }),
    }).catch(function () {});
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'b2b_browse_uncontracted', {
        company_id: companyId,
        company_name: companyName,
        item_name: priceInfo && priceInfo.title,
      });
    }
  }

  function fill() {
    var nodes = [].slice.call(document.querySelectorAll('[data-b2b-price]'));
    if (!nodes.length) return;
    var ids = [...new Set(nodes.map(function (n) { return n.dataset.variantId; }).filter(Boolean))];
    if (!ids.length) return;
    var ac = new AbortController();
    // Watchdog: a hung fetch (no response, no rejection) would leave the CSS
    // shimmer running forever. Abort after 8s so the existing fail-closed
    // .catch runs (→ data-b2b-signin, buy button gated) instead.
    var watchdog = setTimeout(function () { ac.abort(); }, 8000);
    fetch(withTok(PREFIX + '/prices?variants=' + encodeURIComponent(ids.join(','))), { signal: ac.signal })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        if (!res.ok || !res.d.ok) throw new Error('unauthenticated');
        var byId = new Map(res.d.prices.map(function (p) { return [p.variantId, p]; }));
        var anyContracted = false;
        var anyRetail = false;
        var retailInfo = null;
        nodes.forEach(function (n) {
          var p = byId.get(n.dataset.variantId);
          if (!p) {
            n.removeAttribute('data-b2b-filled');
            n.setAttribute('data-b2b-signin', '');
            return;
          }
          if (p.source === 'retail') {
            anyRetail = true;
            retailInfo = p;
            n.removeAttribute('data-b2b-signin');
            n.setAttribute('data-b2b-filled', '');
            return;
          }
          anyContracted = true;
          var priceEl = n.querySelector('.price-item--regular, .price-item') || n.firstElementChild || n;
          var incTxt = '$' + parseFloat(p.price).toFixed(2);
          var exTxt = '$' + (parseFloat(p.price) / 1.1).toFixed(2);
          // Header GST switcher (2026-10-19): the price node carries both an
          // inc and an ex span and CSS picks one on html[data-gst-mode]. Fill
          // both so the contract price follows the buyer's chosen basis.
          var incSpan = priceEl.querySelector('[data-mg-price-inc]');
          var exSpan = priceEl.querySelector('[data-mg-price-ex]');
          if (incSpan && exSpan) {
            incSpan.innerHTML = incTxt + ' <small class="mg-price__gst-label">inc. GST</small>';
            exSpan.innerHTML = exTxt + ' <small class="mg-price__gst-label">ex. GST</small>';
          } else {
            priceEl.textContent = incTxt;
          }
          // The ex-GST line under the price is Liquid-rendered from retail, so
          // it has to move with the contract price or it contradicts it.
          var exEl = n.querySelector('[data-b2b-ex]');
          if (exEl) exEl.textContent = '(' + exTxt + ' ex. GST)';
          var incEl = n.querySelector('[data-b2b-inc]');
          if (incEl) incEl.textContent = '(' + incTxt + ' inc. GST)';
          // Never leave a retail figure sitting behind the contract price.
          n.querySelectorAll('.price-item--sale, .price-item--compare').forEach(function (el) { el.remove(); });
          n.removeAttribute('data-b2b-signin');
          n.setAttribute('data-b2b-filled', '');
        });
        document.documentElement.dataset.b2bVariantContracted = anyContracted ? '1' : '0';
        if (anyRetail && !anyContracted) trackUncontractedVisit(res.d.company, res.d.companyName, retailInfo);
      })
      .catch(function () {
        nodes.forEach(function (n) {
          n.removeAttribute('data-b2b-filled');
          n.setAttribute('data-b2b-signin', '');
        });
        // Fail closed, matching the price display's own failure state: a
        // fetch failure means we genuinely don't know if this variant is
        // contracted, and guessing "not contracted" risks a contracted buyer
        // completing a native checkout at retail during a transient blip.
        // Guessing "contracted" only costs an extra click through to the
        // trade portal, which is the safe direction to be wrong in.
        document.documentElement.dataset.b2bVariantContracted = '1';
      })
      .finally(function () { clearTimeout(watchdog); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fill);
  else fill();

  // Re-fill on variant change. Dawn re-renders the PDP's price markup from the
  // server on a variant switch (a fresh [data-b2b-price] node with the new
  // variant id replaces the old one), so watching for that DOM swap is more
  // robust than coupling to Dawn's internal custom-event names, which drift
  // across theme updates.
  var target = document.querySelector('main') || document.body;
  var scheduled = null;
  new MutationObserver(function () {
    clearTimeout(scheduled);
    scheduled = setTimeout(fill, 60);
  }).observe(target, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-variant-id'] });
})();
